import { defineRecipe } from '@chakra-ui/react';
import colors from 'src/theme/colors';
import fonts from 'src/theme/fonts';
import hexToRgb from 'src/utils/hexToRgb';

const base = {
  color: colors.white,
  fontFamily: fonts.bold,
  textShadow: '0px 0px 2px rgba(0, 0, 0, 0.27)',
  iconSpacing: '2px',
  fontWeight: 'normal !important',
  icon: {
    width: 'unset',
    height: 'unset',
  },
};

const toolButtonStyle = {
  borderRadius: '10px',
  px: '10px',
  py: '3px',
  h: '30px',
};

const Button = defineRecipe({
  base,
  variants: {
    size: {
      xs: {
        fontSize: '12px',
        lineHeight: '18px',
        h: 'unset',
        px: '10px',
        py: '2px',
        borderRadius: '6px',
      },
      sm: {
        fontSize: '14px',
        lineHeight: '18px',
        h: 'unset',
        px: '16px',
        py: '7px',
        borderRadius: '8px',
      },
      md: {
        fontSize: '15px',
        lineHeight: '24px',
        h: 'unset',
        px: '20px',
        py: '8px',
        borderRadius: '8px',
      },
      lg: {
        fontSize: '17px',
        lineHeight: '24px',
        h: 'unset',
        px: '24px',
        py: '11px',
        borderRadius: '12px',
      },
      xl: {
        fontSize: '17px',
        lineHeight: '24px',
        h: 'unset',
        px: '30px',
        py: '14px',
        borderRadius: '12px',
      },
    },
    visual: {
      primary: {
        background: `linear-gradient(${colors.podcastPurple} 0%, ${colors.podcastPurple800} 77.08%)`,
        _hover: {
          background: `linear-gradient(${colors.podcastPurple800} 0%, ${colors.podcastPurple800} 100%)`,
          _disabled: {
            bgGradient: 'to-b',
            gradientFrom: 'podcastPurple',
            gradientTo: 'podcastPurple800',
          },
        },
        _expanded: {
          background: `linear-gradient(${colors.podcastPurple800} 0%, ${colors.podcastPurple800} 100%)`,
        },
      },
      secondary: {
        bg: `linear-gradient(${colors.podcastBlue} 0%, ${colors.podcastBlue600} 82.81%)`,
        _hover: {
          background: `linear-gradient(${colors.podcastBlue600} 0%, ${colors.podcastBlue600} 100%)`,

          _disabled: {
            bgGradient: 'to-b',
            gradientFrom: 'podcastBlue',
            gradientTo: 'podcastBlue600',
          },
        },
        _expanded: {
          background: `linear-gradient(${colors.podcastBlue600} 0%, ${colors.podcastBlue600} 100%)`,
        },
      },
      destructive: {
        bg: `linear-gradient(${colors.podcastRed600} 0%, ${colors.podcastRed} 77.08%)`,
        _hover: {
          background: `linear-gradient(${colors.podcastRed} 0%, ${colors.podcastRed} 100%)`,
          _disabled: {
            bgGradient: 'to-b',
            gradientFrom: 'podcastRed600',
            gradientTo: 'podcastRed',
          },
        },
        _expanded: {
          background: `linear-gradient(${colors.podcastRed} 0%, ${colors.podcastRed} 100%)`,
        },
      },
      tertiary: {
        color: 'podcastShade700',
        textShadow: 'none',
        transition: '200ms ease-in',
        bg: colors.white,
        border: `1px solid ${colors.podcastShade300}`,
        boxShadow: `inset 0px 0px 0px 0px ${colors.podcastShade300}, inset 0px -2px 0px ${colors.podcastShade300}, inset 0px -3px 10px ${colors.podcastShade200}`,
        _hover: {
          boxShadow: `inset 0px -2px 0px ${colors.podcastShade300}`,
          bg: colors.white,
        },
        _expanded: {
          bg: colors.white,
        },
      },
      quaternary: {
        bg: colors.podcastPurple100,
        color: colors.podcastPurple,
        textShadow: 'none',
        _hover: {
          bg: colors.podcastPurple100,
          opacity: 0.7,
          _disabled: {
            bg: colors.podcastPurple100,
            opacity: 0.5,
          },
        },
      },
      orange: {
        bg: 'podcastOrange',
        bgGradient: 'to-b',
        gradientFrom: 'podcastOrange300',
        gradientTo: 'podcastOrange',
        _hover: {
          bg: 'podcastOrange',
          _disabled: {
            bgGradient: 'to-b',
            gradientFrom: 'podcastOrange',
            gradientTo: 'podcastOrange500',
          },
        },
      },
      input: {
        bg: 'podcastPurple800',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        paddingInline: '24px',
        height: '48px',
        bgGradient: 'to-b',
        gradientFrom: 'podcastPurple',
        gradientTo: 'podcastPurple800',
        _hover: {
          _disabled: {
            bgGradient: 'to-b',
            gradientFrom: 'podcastPurple',
            gradientTo: 'podcastPurple800',
          },
        },
      },
      empty: {
        bg: 'none',
        borderRadius: 0,
        margin: 0,
        padding: 0,
        textShadow: 'none',
        color: 'podcastShade500',
        _hover: { bg: 'none' },
        _expanded: { bg: 'none' },
      },
      flat: {
        boxShadow: 'none',
        textTransform: 'uppercase',
        textShadow: 'none',
        _hover: { bgColor: 'podcastPurple' },
        _active: { bgColor: 'podcastPurple800' },
        _expanded: { bgColor: 'podcastPurple800' },
      },
      'flat-light-purple': {
        bgGradient: 'none',
        boxShadow: 'none',
        bgColor: hexToRgb(colors.podcastPurple, 0.1),
        _hover: { bgColor: hexToRgb(colors.podcastPurple, 0.05) },
        _active: { bgColor: hexToRgb(colors.podcastPurple, 0.15) },
        _expanded: { bgColor: hexToRgb(colors.podcastPurple, 0.15) },
      },
      file: {
        height: '30px',
        fontSize: '13px',
        lineHeight: '18px',
        textShadow: 'none',
        fontFamily: fonts.bold,
        borderRadius: '4px',
        cursor: 'initial',
      },
      help: {
        bgGradient: 'none',
        boxShadow: 'none',
        bgColor: hexToRgb(colors.podcastPurple, 0.1),
        _hover: { bgColor: hexToRgb(colors.podcastPurple, 0.05) },
        _active: { bgColor: hexToRgb(colors.podcastPurple, 0.15) },
        color: 'podcastPurple',
        textShadow: 'none',
      },
      cutTool: {
        ...toolButtonStyle,
        bgColor: 'podcastRed600',
        _hover: { bgColor: 'podcastRed400' },
        _active: { bgColor: 'podcastRed' },
      },
      clipTool: {
        ...toolButtonStyle,
        bgColor: 'podcastPurple',
        _hover: { bgColor: 'podcastPurple500' },
        _active: { bgColor: 'podcastPurple800' },
      },
      muteTool: {
        ...toolButtonStyle,
        bgColor: 'podcastRed600',
        _hover: { bgColor: 'podcastRed400' },
        _active: { bgColor: 'podcastRed' },
      },
      silenceTool: {
        ...toolButtonStyle,
        bgColor: 'podcastBlue600',
        _hover: { bgColor: 'podcastBlue' },
        _active: { bgColor: 'podcastBlue700' },
      },
      bleepTool: {
        ...toolButtonStyle,
        bgColor: 'podcastOrange',
        _hover: { bgColor: 'podcastOrange500' },
        _active: { bgColor: 'podcastOrange300' },
      },
      alituWavesTool: {
        minW: '62px',
        bg: 'white',
        borderRadius: '0px',
        p: '4px',
      },
      darkGrey: {
        borderRadius: '12px',
        bgColor: 'podcastShade600',
        _hover: { bgColor: 'podcastShade500' },
        _active: { bgColor: 'podcastShade700' },
        p: '16px',
        h: '36px',
        w: '36px',
        minW: '36px',
      },
      transcriptEditMarker: {
        bg: 'podcastShade200',
        color: 'podcastShade500',
        textShadow: 'none',
        fontSize: '13px',
        lineHeight: '18px',
        h: 'unset',
        px: '4px',
        py: '0',
        borderRadius: '6px',
      },
      link: {
        bg: 'none',
        color: 'podcastPurple',
        textShadow: 'none',
        textDecoration: 'underline',
        p: 0,
        _hover: { bg: 'none' },
      },
      videoOverlay: {
        color: 'podcastShade700',
        textShadow: 'none',
        transition: '200ms ease-in',
        bg: colors.white,
        border: `2px solid ${colors.podcastShade300}`,
        _hover: {
          bg: colors.white,
          outline: `1px solid ${colors.podcastShade300}`,
        },
        _expanded: {
          bg: colors.white,
        },
      },
      unstyled: {},
    },
  },
  defaultVariants: {
    visual: 'primary',
    size: 'md',
  },
});

export default Button;
